import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Calendar, Users, Euro, FileText, Plus, UserPlus,
  Clock, CheckCircle2, ArrowRight, Check, X, Phone,
} from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";

const KpiCard = ({ icon: Icon, label, value, tint, sub, testid }) => (
  <Card data-testid={testid} className="p-5 sm:p-6 rounded-xl border-slate-200 bg-white shadow-sm hover:shadow-md transition-all duration-200">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
        <p className="mt-2 font-heading text-3xl font-bold text-slate-900 font-mono-data">{value}</p>
        {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
      </div>
      <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ backgroundColor: `${tint}1a` }}>
        <Icon className="h-5 w-5" style={{ color: tint }} />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const quickActions = [
    { label: "Nuovo Paziente", icon: UserPlus, to: "/pazienti", testid: "btn-nuovo-paziente" },
    { label: "Nuovo Appuntamento", icon: Plus, to: "/calendario", testid: "btn-nuovo-appuntamento" },
    { label: "Crea Preventivo", icon: FileText, to: "/preventivi", testid: "btn-crea-preventivo" },
    { label: "Registra Incasso", icon: Euro, to: "/incassi", testid: "btn-registra-incasso" },
  ];

  const statusColor = { confermato: "#10B981", in_attesa: "#F59E0B", annullato: "#EF4444", completato: "#3B82F6" };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-muted-foreground">{format(new Date(), "EEEE d MMMM yyyy", { locale: it })}</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
          Ciao, {user?.name?.split(" ")[0]} 👋
        </h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {!stats ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)
        ) : (
          <>
            <KpiCard testid="kpi-appuntamenti-oggi" icon={Calendar} tint="#17B8B0" label="Appuntamenti Oggi"
                     value={stats.appuntamenti_oggi}
                     sub={`${stats.appuntamenti_confermati} confermati · ${stats.appuntamenti_in_attesa} in attesa`} />
            <KpiCard testid="kpi-pazienti-attivi" icon={Users} tint="#3B82F6" label="Pazienti Attivi"
                     value={stats.pazienti_attivi} sub={`${stats.pazienti_totali} totali`} />
            <KpiCard testid="kpi-incassi-mese" icon={Euro} tint="#10B981" label="Incassi Mese"
                     value={`€ ${stats.incassi_mese.toLocaleString("it-IT")}`} />
            <KpiCard testid="kpi-collaboratori" icon={Users} tint="#F59E0B" label="Collaboratori Attivi"
                     value={stats.collaboratori_attivi} />
          </>
        )}
      </div>

      {/* Preventivi box */}
      <Card className="p-5 sm:p-6 rounded-xl border-slate-200 bg-white shadow-sm" data-testid="preventivi-box">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <h2 className="font-heading text-lg font-semibold text-slate-800">Preventivi</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/preventivi")} className="text-primary">
            Gestisci <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
        {!stats ? (
          <div className="grid grid-cols-3 gap-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <button onClick={() => navigate("/preventivi")} data-testid="box-preventivi-accettati"
                    className="group flex items-center gap-4 rounded-xl border border-emerald-100 bg-emerald-50/50 p-4 text-left transition-all hover:border-emerald-300 hover:shadow-sm">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100"><Check className="h-6 w-6 text-emerald-600" /></div>
              <div>
                <p className="font-heading text-3xl font-bold text-emerald-700 font-mono-data leading-none">{stats.preventivi_accettati}</p>
                <p className="mt-1 text-sm font-medium text-slate-600">Accettati</p>
              </div>
            </button>
            <button onClick={() => navigate("/preventivi")} data-testid="box-preventivi-contatto"
                    className="group flex items-center gap-4 rounded-xl border border-amber-100 bg-amber-50/50 p-4 text-left transition-all hover:border-amber-300 hover:shadow-sm">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100"><Phone className="h-6 w-6 text-amber-600" /></div>
              <div>
                <p className="font-heading text-3xl font-bold text-amber-700 font-mono-data leading-none">{stats.preventivi_contatto}</p>
                <p className="mt-1 text-sm font-medium text-slate-600">Da richiamare</p>
              </div>
            </button>
            <button onClick={() => navigate("/preventivi")} data-testid="box-preventivi-rifiutati"
                    className="group flex items-center gap-4 rounded-xl border border-red-100 bg-red-50/50 p-4 text-left transition-all hover:border-red-300 hover:shadow-sm">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-100"><X className="h-6 w-6 text-red-600" /></div>
              <div>
                <p className="font-heading text-3xl font-bold text-red-700 font-mono-data leading-none">{stats.preventivi_rifiutati}</p>
                <p className="mt-1 text-sm font-medium text-slate-600">Rifiutati</p>
              </div>
            </button>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's appointments */}
        <Card className="lg:col-span-2 p-5 sm:p-6 rounded-xl border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold text-slate-800">Agenda di oggi</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate("/calendario")} className="text-primary">
              Calendario <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
          {!stats ? (
            <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}</div>
          ) : stats.appuntamenti_oggi_lista.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              <Calendar className="mx-auto h-8 w-8 text-slate-300 mb-2" />
              Nessun appuntamento in programma per oggi.
            </div>
          ) : (
            <div className="space-y-2" data-testid="today-appointments">
              {stats.appuntamenti_oggi_lista.map((a) => (
                <div key={a.id} className="flex items-center gap-4 rounded-lg border border-slate-100 bg-slate-50/60 p-3 hover:bg-slate-50 transition-colors">
                  <div className="flex flex-col items-center justify-center rounded-md bg-white px-3 py-1.5 shadow-sm border border-slate-100 min-w-[62px]">
                    <span className="font-mono-data text-sm font-semibold text-slate-800">{format(new Date(a.inizio), "HH:mm")}</span>
                    <span className="text-[10px] text-slate-400">{format(new Date(a.fine), "HH:mm")}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-slate-800">{a.client_nome}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {a.service_nome || "—"}{a.collaborator_nome ? ` · ${a.collaborator_nome}` : ""}
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium"
                        style={{ backgroundColor: `${statusColor[a.stato]}1a`, color: statusColor[a.stato] }}>
                    {a.stato === "confermato" ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                    {a.stato.replace("_", " ")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Quick actions */}
        <Card className="p-5 sm:p-6 rounded-xl border-slate-200 bg-white shadow-sm">
          <h2 className="font-heading text-lg font-semibold text-slate-800 mb-4">Azioni rapide</h2>
          <div className="grid grid-cols-2 gap-3">
            {quickActions.map(({ label, icon: Icon, to, testid }) => (
              <button key={to} onClick={() => navigate(to)} data-testid={testid}
                      className="group flex flex-col items-start gap-3 rounded-xl border border-slate-200 p-4 text-left transition-all hover:border-primary hover:bg-primary/5">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                  <Icon className="h-5 w-5" />
                </div>
                <span className="text-sm font-medium text-slate-700 leading-tight">{label}</span>
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
